import { isAxiosError } from "axios"
import { getUUID } from "./utils"
import { unlessVitestEnv } from "./vitest"
import DOMPurify from "dompurify"

export type ToastType = 'default' | 'loading' | 'success' | 'warning' | 'error'

export type ToastParams = {
  message: string
  type?: ToastType
  icon?: string
  inverted?: boolean
}

export type FutureToastParams = ToastParams & { id: string }

class FutureToast {
  readonly #id: string

  #params: ToastParams

  constructor (id: string, params: ToastParams) {
    this.#id = id
    this.#params = params
  }

  update (params: ToastParams) {
    this.#params = params

    this.#persist()
  }

  remove () {
    this.#remove()
  }

  set inverted(value: boolean | undefined) {
    this.#params.inverted = value

    this.#persist()
  }

  get inverted () {
    return this.#params.inverted
  }

  set type(type: ToastType) {
    this.#params.type = type

    this.#persist()
  }

  get type () {
    return this.#params.type ?? 'default'
  }

  set icon(icon: string | undefined) {
    this.#params.icon = icon

    this.#persist()
  }

  get icon () {
    return this.#params.icon
  }

  set message(message: string) {
    this.#params.message = message

    this.#persist()
  }

  get message () {
    return this.#params.message
  }

  #remove () {
    V_TOAST_STATE.futureToasts = V_TOAST_STATE.futureToasts.filter((toast) => toast.id !== this.#id)
  }

  #persist () {
    const toasts = V_TOAST_STATE.futureToasts

    const toastIndex = toasts.findIndex((toast) => toast.id === this.#id)
    if (toastIndex !== -1) {
      toasts[toastIndex] = {
        ...this.#params,
        id: this.#id
      }

      V_TOAST_STATE.futureToasts = toasts
    }
  }
}

class Toast {
  readonly #element: HTMLElement
  readonly #textElement: HTMLElement
  readonly #iconElement: HTMLElement

  #timeout1?: NodeJS.Timeout
  #timeout2?: NodeJS.Timeout

  constructor(element: HTMLElement, params: ToastParams) {
    this.#element = element
    this.#textElement = element.querySelector('.avv-toast-text') as HTMLElement
    this.#iconElement = element.querySelector(
      '.avv-toast-icon > i'
    ) as HTMLElement

    this.update(params)

    setTimeout(() => this.#element.classList.add('visible'), 0)
  }

  update(params: ToastParams) {
    this.type = params.type ?? 'default'
    this.icon = params.icon
    this.message = params.message
    this.inverted = params.inverted
  }

  remove() {
    this.#stopDecay()

    this.#element.remove()
  }

  set inverted(value: boolean | undefined) {
    if (value) {
      this.#element.classList.add('with-inverted-colors')
    } else {
      this.#element.classList.remove('with-inverted-colors')
    }
  }

  set type(type: ToastType) {
    this.#element.setAttribute('data-type', type)

    if (type === 'loading') {
      this.#stopDecay()
    } else {
      this.#startDecay()
    }
  }

  set icon(icon: string | undefined) {
    if (typeof icon === 'string') {
      this.#element.classList.add('with-icon')

      this.#iconElement.innerText = icon
    } else {
      this.#element.classList.remove('with-icon')
    }
  }

  set message(message: string) {
    const formattedMessage = message.replace(/\[([^\]]+?)\]\(([^\)]+?)\)/gi, (_, text, url) => {
      const node = document.createElement('a')
      node.href = url
      node.setAttribute('target', '_blank')
      node.innerText = text

      return node.outerHTML
    })

    this.#textElement.innerHTML = DOMPurify.sanitize(formattedMessage, {
      ADD_ATTR: ['target']
    })
  }

  #startDecay() {
    this.#timeout1 = setTimeout(
      () => this.#element.classList.remove('visible'),
      2000
    )
    this.#timeout2 = setTimeout(() => this.#element.remove(), 2500)
  }

  #stopDecay() {
    clearTimeout(this.#timeout1)
    clearTimeout(this.#timeout2)
  }
}

const FUTURE_TOAST_LS_KEY = 'avvokaFutureToasts'

const V_TOAST_STATE: {
  // Init
  __container?: HTMLElement

  // Public
  get container (): HTMLElement
  get futureToasts(): FutureToastParams[]
  set futureToasts(toasts: FutureToastParams[])
} = {
  get container () {
    return this.__container ??= (() => {
      let element = document.getElementById('v-toast-container')
      if (element) return element
  
      element = document.createElement('div')
      element.id = 'v-toast-container'
      element.setAttribute('class', 'avv-toast-container')
  
      document.body.appendChild(element)
  
      return element
    })()
  },
  get futureToasts () {
    return JSON.parse(localStorage.getItem(FUTURE_TOAST_LS_KEY) ?? '[]') as FutureToastParams[]
  },
  set futureToasts (toasts: FutureToastParams[]) {
    localStorage.setItem(FUTURE_TOAST_LS_KEY, JSON.stringify(toasts))
  }
}

unlessVitestEnv(() => {
  window.addEventListener('DOMContentLoaded', () => {
    for (const toast of V_TOAST_STATE.futureToasts) {
      useToast(toast)
    }
  
    V_TOAST_STATE.futureToasts = []
  })
})

export function useToast (params: ToastParams, options: { later: true }): FutureToast
export function useToast (params: ToastParams, options: { later?: boolean }): Toast
export function useToast (params: ToastParams): Toast

export function useToast (params: ToastParams, options: { later?: boolean } = {}): Toast | FutureToast {
  const id = getUUID()

  if (options.later) {
    V_TOAST_STATE.futureToasts = V_TOAST_STATE.futureToasts.concat([
      {
        id,
        ...params
      }
    ])

    return new FutureToast(id, params)
  } else {
    const element = document.createElement('div') as HTMLDivElement & {
      $toast: Toast
    }

    element.classList.add('avv-toast')
    element.id = id
    element.innerHTML = '<div class="avv-toast-icon"><i aria-hidden="true" class="material-symbols-outlined"></i></div><div class="avv-toast-text"></div>'

    // Insert into container
    V_TOAST_STATE.container.insertAdjacentElement('afterbegin', element)
  
    // Return as toast object
    const toast = new Toast(element, params)

    element.$toast = toast
  
    return toast
  }
}

export function useErrorToast (errorOrMessage: unknown, options: { later?: boolean } = {}) {
  const message = (isAxiosError<{ error: string }>(errorOrMessage) ? errorOrMessage.response?.data.error : undefined) ?? (
    errorOrMessage instanceof Error ? errorOrMessage.message : errorOrMessage as string
  )

  return useToast({
    type: 'error',
    message
  }, options)
}
